import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'

type DrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: ReactNode
  description?: ReactNode
  footer?: ReactNode
  children: ReactNode
  width?: 'sm' | 'md' | 'lg' | 'xl'
}

const widthMap = {
  sm: 'w-[400px]',
  md: 'w-[560px]',
  lg: 'w-[720px]',
  xl: 'w-[900px]',
}

export function Drawer({ open, onOpenChange, title, description, footer, children, width = 'md' }: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in data-[state=closed]:fade-out" />
        <Dialog.Content
          className={cn(
            'fixed right-0 top-0 z-50 h-full max-w-[95vw] bg-bg-elevated border-l border-border shadow-lg flex flex-col',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            widthMap[width],
          )}
        >
          <div className="flex items-start justify-between px-5 py-4 border-b border-border">
            <div>
              {title && <Dialog.Title className="text-base font-semibold tracking-tight">{title}</Dialog.Title>}
              {description && (
                <Dialog.Description className="text-xs text-fg-subtle mt-0.5">{description}</Dialog.Description>
              )}
            </div>
            <Dialog.Close className="ml-4 rounded p-1 text-fg-subtle hover:text-fg hover:bg-bg-muted">
              <X size={16} />
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="border-t border-border px-5 py-3 bg-bg">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
